// V20 — Consolidated Report Layer
//
// Renders the consolidated BS/IS/CF/T12 for a consolidated_group client
// (or an ad-hoc multi-client selection). Identity guarantees are enforced
// by the merge helpers in lib/reports.js:
//   • Consolidated CF identity = sum of per-member identities (exact $0.00
//     by construction since each member's identity is exact — Rule 4).
//   • Consolidated BS balances by the same argument.
//
// Member-level statements are produced from each member's `accounts +
// mapping` payload returned by /api/consolidated/{group_id}/data. The
// merge runs at the structured-builder-output level (by canonical line
// LABEL, case-insensitive).

import React, { useMemo, useState } from 'react';
import { Building2, Download, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  buildConsolidated, runConsolidatedValidationTies, defaultPeriodLabels,
  getStatementTitle, getEffectiveFramework,
} from '../../lib/reports.js';
import { StructuredItems, formatCurrency } from './StructuredSection.js';
import PrintModal from '../PrintModal.js';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const COLORS = {
  primary: '#0085CA',
  primaryLight: '#E0F7FA',
  dark: '#1A2C42',
  muted: '#6B7C93',
  border: '#E1E8ED',
  subtle: '#F5F7FA',
  card: '#FFFFFF',
  success: '#1F9D57',
  danger: '#C8322B',
  warn: '#F59E0B',
};

function fmt(v) {
  if (v === undefined || v === null || Number.isNaN(v)) return '—';
  return formatCurrency(v);
}

function memberCountLabel(n) {
  return n === 1 ? '1 member' : `${n} members`;
}

// Simple statement row component used by StructuredItems
function StatementRow({ label, value, priorValue, indent = 1, isGroupHeader,
                       isGroupSubtotal, isSectionTotal, ...rest }) {
  return (
    <tr {...rest} style={{
      borderBottom: isSectionTotal ? `2px solid ${COLORS.dark}` : `1px solid ${COLORS.subtle}`,
      fontWeight: (isGroupHeader || isGroupSubtotal || isSectionTotal) ? 600 : 400,
      background: isSectionTotal ? COLORS.subtle : 'transparent',
    }}>
      <td style={{ padding: '6px 10px', paddingLeft: 10 + indent * 16, fontSize: 13 }}>
        {label}
      </td>
      <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
        {isGroupHeader ? '' : fmt(value)}
      </td>
      {priorValue !== undefined && (
        <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: COLORS.muted }}>
          {isGroupHeader ? '' : fmt(priorValue)}
        </td>
      )}
    </tr>
  );
}

function SectionTable({ title, periodLabel, items, total, totalPrior, showPrior,
                       totalLabel = 'Total', columnLabels }) {
  return (
    <div data-testid={`consolidated-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
         style={{ marginBottom: 28 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
            <th style={{ textAlign: 'left', padding: '10px', fontSize: 13, fontWeight: 700, color: COLORS.dark }}>
              {title}
              {periodLabel && <span style={{ fontWeight: 400, color: COLORS.muted, marginLeft: 12 }}>{periodLabel}</span>}
            </th>
            <th style={{ textAlign: 'right', padding: '10px', fontSize: 11, fontWeight: 600, color: COLORS.muted, width: 140 }}>
              {columnLabels?.[0] || 'Current'}
            </th>
            {showPrior && (
              <th style={{ textAlign: 'right', padding: '10px', fontSize: 11, fontWeight: 600, color: COLORS.muted, width: 140 }}>
                {columnLabels?.[1] || 'Prior'}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          <StructuredItems items={items || []} RowComponent={(p) => (
            <StatementRow {...p} priorValue={showPrior ? p.priorValue : undefined} />
          )} showPrior={showPrior} baseIndent={1} />
          <StatementRow
            label={totalLabel}
            value={total}
            priorValue={showPrior ? totalPrior : undefined}
            indent={0}
            isSectionTotal
          />
        </tbody>
      </table>
    </div>
  );
}

export default function ConsolidatedReportLayer({ envelope, group, client }) {
  const [activeReport, setActiveReport] = useState('bs');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  // memberPayloads in the shape buildConsolidated expects
  const memberPayloads = useMemo(() => {
    if (!envelope?.members) return [];
    return envelope.members
      .filter((m) => m.accounts_payload && m.mapping && !m.accounts_error)
      .map((m) => ({
        client: m.client,
        accounts: m.accounts_payload.accounts || [],
        mapping: m.mapping,
        ttm: m.ttm,
        qboMeta: {
          ni_at_period_start: m.accounts_payload.ni_at_period_start,
          cash_at_period_start: m.accounts_payload.cash_at_period_start,
          period_start: m.accounts_payload.period_start,
          period_end: m.accounts_payload.period_end,
        },
      }));
  }, [envelope]);

  const ttmMonthLabels = useMemo(() => {
    // Pick the first member's TTM month labels — they should all match if periods align
    const firstWithTtm = envelope?.members?.find((m) => m.ttm?.months);
    if (!firstWithTtm) return [];
    return (firstWithTtm.ttm.months || []).map((m) => `${m.month} ${String(m.year).slice(-2)}`);
  }, [envelope]);

  const consolidated = useMemo(
    () => buildConsolidated(memberPayloads, ttmMonthLabels),
    [memberPayloads, ttmMonthLabels],
  );

  const validation = useMemo(
    () => runConsolidatedValidationTies(consolidated),
    [consolidated],
  );

  const periodEnd = envelope?.period_end;
  const periodLabels = defaultPeriodLabels({ period_end: periodEnd });
  const periodLabel = periodLabels.monthsEndedLabel || periodLabels.asOfLabel;

  // Build a synthetic "client" object so PrintModal renders correct masthead
  const printClient = {
    ...client,
    company: group?.company || client?.company || 'Consolidated Group',
    name: group?.name || client?.name || '',
    framework: group?.framework || 'gaap',
    framework_other_label: group?.framework_other_label,
    engagement_type: group?.engagement_type || 'preparation',
    disclosures_omitted: group?.disclosures_omitted ?? true,
    independent: group?.independent ?? true,
    cf_materiality_threshold: group?.cf_materiality_threshold,
    bs_py_comparison_basis: group?.bs_py_comparison_basis || 'prior_year_same_date',
  };

  const handleExportPDF = async () => {
    if (!group?.id) {
      alert('PDF export is currently supported only for persistent consolidated groups (not ad-hoc previews).');
      return;
    }
    setExporting(true);
    try {
      const token = localStorage.getItem('smj_token');
      const resp = await axios.post(
        `${API_URL}/pdf/generate-consolidated`,
        { group_id: group.id, package: 'package', report_type: 'prepared',
          period_end: periodEnd, disposition: 'attachment' },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(group?.company || 'consolidated').replace(/\s+/g, '_')}-consolidated.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Consolidated PDF export failed:', err);
      let detail = err?.response?.data;
      if (detail instanceof Blob) {
        try {
          const text = await detail.text();
          detail = JSON.parse(text);
        } catch (_) { /* keep blob */ }
      }
      const msg = detail?.detail || err.message || 'Unknown error';
      alert(`Consolidated PDF export failed: ${typeof msg === 'string' ? msg : JSON.stringify(msg)}`);
    } finally {
      setExporting(false);
    }
  };

  if (!consolidated || memberPayloads.length === 0) {
    return (
      <div style={{ background: COLORS.card, padding: 32, borderRadius: 12, border: `1px solid ${COLORS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <AlertTriangle size={24} color={COLORS.warn} />
          <h2 style={{ margin: 0, color: COLORS.dark }}>Consolidated group not ready</h2>
        </div>
        <p style={{ color: COLORS.muted, fontSize: 14 }}>
          Every member must be QBO-connected and have a saved canonical mapping
          before the consolidation can render.
        </p>
        {envelope?.warnings?.missing_qbo_members?.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <strong style={{ color: COLORS.dark, fontSize: 13 }}>QBO not connected:</strong>
            <ul style={{ marginTop: 6, paddingLeft: 24 }}>
              {envelope.warnings.missing_qbo_members.map((c) => (
                <li key={c} data-testid="missing-qbo-member" style={{ color: COLORS.muted, fontSize: 13 }}>{c}</li>
              ))}
            </ul>
          </div>
        )}
        {envelope?.warnings?.unmapped_members?.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <strong style={{ color: COLORS.dark, fontSize: 13 }}>Mapping required:</strong>
            <ul style={{ marginTop: 6, paddingLeft: 24 }}>
              {envelope.warnings.unmapped_members.map((c) => (
                <li key={c} data-testid="unmapped-member" style={{ color: COLORS.muted, fontSize: 13 }}>{c}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Top tabs
  const tabs = [
    { id: 'bs', label: 'Balance Sheet' },
    { id: 'pl', label: 'Income Statement' },
    { id: 'cf', label: 'Cash Flow' },
    { id: 'bs_py', label: 'BS PY' },
    { id: 'pl_py', label: 'IS PY' },
    { id: 't12', label: 'T12' },
  ];

  const { is, bs, cf, t12 } = consolidated;

  return (
    <div style={{ padding: '0' }} data-testid="consolidated-report-layer">
      {/* Members summary strip */}
      <div style={{ background: COLORS.card, borderRadius: 12, padding: 20,
                    border: `1px solid ${COLORS.border}`, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Building2 size={22} color={COLORS.primary} />
              <h2 style={{ margin: 0, fontSize: 20, color: COLORS.dark }}>
                {printClient.company}
              </h2>
              <span style={{ padding: '2px 10px', background: COLORS.primaryLight,
                             color: COLORS.primary, borderRadius: 8, fontSize: 11, fontWeight: 600 }}>
                Consolidated · {memberCountLabel(memberPayloads.length)}
              </span>
            </div>
            <div style={{ color: COLORS.muted, fontSize: 13, marginTop: 6 }}>{periodLabel}</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              data-testid="consolidated-print-btn"
              onClick={() => setShowPrintModal(true)}
              disabled={!validation.allGreen}
              title={validation.allGreen ? 'Print consolidated package' : 'Resolve consolidated validation ties first'}
              style={{
                padding: '8px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                background: validation.allGreen ? COLORS.primary : COLORS.muted,
                color: '#fff', border: 'none',
                cursor: validation.allGreen ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <FileText size={14} /> Print
            </button>
            <button
              data-testid="consolidated-export-pdf-btn"
              onClick={handleExportPDF}
              disabled={!validation.allGreen || exporting || !group?.id}
              style={{
                padding: '8px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                background: (!validation.allGreen || !group?.id) ? COLORS.muted : COLORS.dark,
                color: '#fff', border: 'none',
                cursor: (!validation.allGreen || exporting || !group?.id) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <Download size={14} /> {exporting ? 'Generating…' : 'Export PDF'}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {memberPayloads.map((m) => (
            <div key={m.client.id} data-testid="consolidated-member-chip"
                 style={{
                   padding: '6px 12px', borderRadius: 6, background: COLORS.subtle,
                   border: `1px solid ${COLORS.border}`, fontSize: 12,
                   display: 'flex', alignItems: 'center', gap: 6,
                 }}>
              <Building2 size={12} color={COLORS.muted} />
              <span style={{ color: COLORS.dark, fontWeight: 600 }}>{m.client.company}</span>
            </div>
          ))}
          {envelope?.warnings?.missing_qbo_members?.length > 0 &&
            envelope.warnings.missing_qbo_members.map((c) => (
              <div key={`missing-${c}`} data-testid="consolidated-warn-chip" style={{
                padding: '6px 12px', borderRadius: 6, background: '#FEF3C7',
                border: `1px solid ${COLORS.warn}`, fontSize: 12,
                display: 'flex', alignItems: 'center', gap: 6,
                color: '#92400E',
              }}>
                <AlertTriangle size={12} /> {c} (no QBO)
              </div>
            ))}
        </div>

        {/* Validation banner */}
        <div data-testid="consolidated-validation-banner" style={{
          marginTop: 16,
          padding: '10px 12px',
          borderRadius: 6,
          background: validation.allGreen ? '#ECFDF5' : '#FEF2F2',
          border: `1px solid ${validation.allGreen ? COLORS.success : COLORS.danger}`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {validation.allGreen
            ? <CheckCircle2 size={18} color={COLORS.success} />
            : <AlertTriangle size={18} color={COLORS.danger} />}
          <div style={{ fontSize: 13, fontWeight: 600,
                        color: validation.allGreen ? '#064E3B' : '#7F1D1D' }}>
            {validation.allGreen
              ? `All ${validation.ties.length} consolidated ties green`
              : `${validation.ties.filter((t) => !t.ok).length} consolidated tie(s) failing`}
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: COLORS.muted, display: 'flex', gap: 12 }}>
            {validation.ties.map((t) => (
              <span key={t.id} title={t.detail || ''}
                    style={{ color: t.ok ? COLORS.success : COLORS.danger }}>
                {t.ok ? '✓' : '✗'} {t.id.replace(/^consolidated_/, '')}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Statement tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${COLORS.border}` }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`consolidated-tab-${t.id}`}
            onClick={() => setActiveReport(t.id)}
            style={{
              padding: '10px 16px', border: 'none', background: 'transparent',
              borderBottom: activeReport === t.id ? `3px solid ${COLORS.primary}` : '3px solid transparent',
              color: activeReport === t.id ? COLORS.primary : COLORS.muted,
              fontWeight: 600, fontSize: 13, cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ background: COLORS.card, borderRadius: 12, padding: 24, border: `1px solid ${COLORS.border}` }}>
        {activeReport === 'bs' && bs && (
          <>
            <h3 data-testid="bs-title" style={{ marginTop: 0, color: COLORS.dark, fontSize: 18 }}>
              {getStatementTitle('bs', getEffectiveFramework(printClient))}
            </h3>
            <div style={{ color: COLORS.muted, fontSize: 13, marginBottom: 16 }}>{periodLabels.asOfLabel}</div>
            <SectionTable title="Cash & Equivalents" items={bs.sections.cash.items}
                          total={bs.sections.cash.total} />
            <SectionTable title="Current Assets" items={bs.sections.assetsCur.items}
                          total={bs.sections.assetsCur.total} />
            <SectionTable title="Fixed Assets" items={bs.sections.assetsFix.items}
                          total={bs.sections.assetsFix.total} />
            <SectionTable title="Other Assets" items={bs.sections.assetsOther.items}
                          total={bs.sections.assetsOther.total} />
            <div data-testid="bs-total-assets" style={{ padding: '12px 16px', background: COLORS.subtle, borderRadius: 6, fontWeight: 700, fontSize: 15, marginBottom: 24, color: COLORS.dark, display: 'flex', justifyContent: 'space-between' }}>
              <span>TOTAL ASSETS</span><span>{fmt(bs.totals.totalAssets)}</span>
            </div>
            <SectionTable title="Current Liabilities" items={bs.sections.liabCur.items}
                          total={bs.sections.liabCur.total} />
            <SectionTable title="Long-Term Liabilities" items={bs.sections.liabLong.items}
                          total={bs.sections.liabLong.total} />
            <SectionTable title="Equity" items={bs.sections.equity.items}
                          total={bs.sections.equity.total} />
            <div data-testid="bs-total-le" style={{ padding: '12px 16px', background: COLORS.subtle, borderRadius: 6, fontWeight: 700, fontSize: 15, color: COLORS.dark, display: 'flex', justifyContent: 'space-between' }}>
              <span>TOTAL LIABILITIES + EQUITY</span><span>{fmt(bs.totals.totalLE)}</span>
            </div>
          </>
        )}

        {activeReport === 'pl' && is && (
          <>
            <h3 data-testid="pl-title" style={{ marginTop: 0, color: COLORS.dark, fontSize: 18 }}>
              {getStatementTitle('pl', getEffectiveFramework(printClient))}
            </h3>
            <div style={{ color: COLORS.muted, fontSize: 13, marginBottom: 16 }}>{periodLabel}</div>
            <SectionTable title="Revenue" items={is.sections.income.items} total={is.totals.totalRevenue} />
            <SectionTable title="Cost of Goods Sold" items={is.sections.cogs.items} total={is.totals.totalCOGS} />
            <div data-testid="pl-gross-profit" style={{ padding: '10px 16px', background: COLORS.subtle, borderRadius: 6, fontWeight: 700, fontSize: 14, marginBottom: 20, color: COLORS.dark, display: 'flex', justifyContent: 'space-between' }}>
              <span>GROSS PROFIT</span><span>{fmt(is.totals.grossProfit)}</span>
            </div>
            <SectionTable title="Operating Expenses" items={is.sections.opex.items} total={is.totals.totalOpEx} />
            <SectionTable title="Other Income" items={is.sections.otherIncome.items} total={is.totals.otherIncome} />
            <SectionTable title="Other Expense" items={is.sections.otherExpense.items} total={is.totals.otherExpense} />
            <div data-testid="pl-net-income" style={{ padding: '12px 16px', background: COLORS.dark, borderRadius: 6, fontWeight: 700, fontSize: 15, color: '#fff', display: 'flex', justifyContent: 'space-between' }}>
              <span>NET INCOME</span><span>{fmt(is.totals.netIncome)}</span>
            </div>
          </>
        )}

        {activeReport === 'cf' && cf && (
          <>
            <h3 data-testid="cf-title" style={{ marginTop: 0, color: COLORS.dark, fontSize: 18 }}>
              {getStatementTitle('cf', getEffectiveFramework(printClient))}
            </h3>
            <div style={{ color: COLORS.muted, fontSize: 13, marginBottom: 16 }}>{periodLabel}</div>
            {['operating', 'investing', 'financing', 'other'].map((sec) => {
              const section = cf.sections?.[sec];
              if (!section) return null;
              const titleMap = {
                operating: 'Cash Flows from Operating Activities',
                investing: 'Cash Flows from Investing Activities',
                financing: 'Cash Flows from Financing Activities',
                other: 'Cash Flows from Other Activities',
              };
              return (
                <div key={sec} data-testid={`cf-section-${sec}`} style={{ marginBottom: 20 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                        <th colSpan="2" style={{ padding: '10px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: COLORS.dark }}>
                          {titleMap[sec]}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.rows.map((row, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${COLORS.subtle}`,
                                             fontWeight: row.subheader ? 700 : 400,
                                             fontStyle: row.rollup ? 'italic' : 'normal' }}>
                          <td style={{ padding: '6px 10px', paddingLeft: 10 + (row.indent || 1) * 16, fontSize: 13 }}>{row.label}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                            {row.subheader ? '' : fmt(row.value)}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: `2px solid ${COLORS.dark}`, background: COLORS.subtle, fontWeight: 700 }}>
                        <td style={{ padding: '8px 10px', fontSize: 13 }}>Net Cash from {titleMap[sec].replace('Cash Flows from ', '')}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                          {fmt(section.subtotal)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })}
            <div data-testid="cf-net-change" style={{ padding: '10px 16px', background: COLORS.subtle, borderRadius: 6, fontWeight: 700, fontSize: 14, color: COLORS.dark, display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>NET CHANGE IN CASH</span><span>{fmt(cf.netChange)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 16px', fontSize: 13, color: COLORS.muted }}>
              <span>Beginning Cash</span><span>{fmt(cf.beginCash)}</span>
            </div>
            <div data-testid="cf-ending-cash" style={{ padding: '10px 16px', background: COLORS.dark, color: '#fff', borderRadius: 6, fontWeight: 700, fontSize: 15, display: 'flex', justifyContent: 'space-between' }}>
              <span>ENDING CASH</span><span>{fmt(cf.endCash)}</span>
            </div>
            {(cf.supplemental?.interestPaid || cf.supplemental?.taxesPaid) && (
              <div style={{ marginTop: 20 }}>
                <h4 style={{ fontSize: 12, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 8px' }}>
                  Supplemental Disclosures
                </h4>
                {cf.supplemental.interestPaid !== 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                    <span>Cash paid for interest</span><span>{fmt(cf.supplemental.interestPaid)}</span>
                  </div>
                )}
                {cf.supplemental.taxesPaid !== 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                    <span>Cash paid for income taxes</span><span>{fmt(cf.supplemental.taxesPaid)}</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeReport === 'bs_py' && bs && (
          <>
            <h3 style={{ marginTop: 0, color: COLORS.dark, fontSize: 18 }}>Balance Sheet — Prior Year Comparison</h3>
            <SectionTable title="Cash & Equivalents" items={bs.sections.cash.items}
                          total={bs.sections.cash.total} totalPrior={bs.sections.cash.total_prior}
                          showPrior columnLabels={['Current', 'Prior Year']} />
            <SectionTable title="Current Assets" items={bs.sections.assetsCur.items}
                          total={bs.sections.assetsCur.total} totalPrior={bs.sections.assetsCur.total_prior}
                          showPrior columnLabels={['Current', 'Prior Year']} />
            <SectionTable title="Fixed Assets" items={bs.sections.assetsFix.items}
                          total={bs.sections.assetsFix.total} totalPrior={bs.sections.assetsFix.total_prior}
                          showPrior columnLabels={['Current', 'Prior Year']} />
            <SectionTable title="Other Assets" items={bs.sections.assetsOther.items}
                          total={bs.sections.assetsOther.total} totalPrior={bs.sections.assetsOther.total_prior}
                          showPrior columnLabels={['Current', 'Prior Year']} />
            <SectionTable title="Current Liabilities" items={bs.sections.liabCur.items}
                          total={bs.sections.liabCur.total} totalPrior={bs.sections.liabCur.total_prior}
                          showPrior columnLabels={['Current', 'Prior Year']} />
            <SectionTable title="Long-Term Liabilities" items={bs.sections.liabLong.items}
                          total={bs.sections.liabLong.total} totalPrior={bs.sections.liabLong.total_prior}
                          showPrior columnLabels={['Current', 'Prior Year']} />
            <SectionTable title="Equity" items={bs.sections.equity.items}
                          total={bs.sections.equity.total} totalPrior={bs.sections.equity.total_prior}
                          showPrior columnLabels={['Current', 'Prior Year']} />
          </>
        )}

        {activeReport === 'pl_py' && is && (
          <>
            <h3 style={{ marginTop: 0, color: COLORS.dark, fontSize: 18 }}>Income Statement — Prior Year Comparison</h3>
            <SectionTable title="Revenue" items={is.sections.income.items}
                          total={is.totals.totalRevenue} totalPrior={is.prior.totalRevenue}
                          showPrior columnLabels={['Current', 'Prior Year']} />
            <SectionTable title="Cost of Goods Sold" items={is.sections.cogs.items}
                          total={is.totals.totalCOGS} totalPrior={is.prior.totalCOGS}
                          showPrior columnLabels={['Current', 'Prior Year']} />
            <SectionTable title="Operating Expenses" items={is.sections.opex.items}
                          total={is.totals.totalOpEx} totalPrior={is.prior.totalOpEx}
                          showPrior columnLabels={['Current', 'Prior Year']} />
            <SectionTable title="Other Income" items={is.sections.otherIncome.items}
                          total={is.totals.otherIncome} totalPrior={is.prior.otherIncome}
                          showPrior columnLabels={['Current', 'Prior Year']} />
            <SectionTable title="Other Expense" items={is.sections.otherExpense.items}
                          total={is.totals.otherExpense} totalPrior={is.prior.otherExpense}
                          showPrior columnLabels={['Current', 'Prior Year']} />
            <div style={{ padding: '12px 16px', background: COLORS.dark, color: '#fff', borderRadius: 6, fontWeight: 700, fontSize: 15, display: 'flex', justifyContent: 'space-between' }}>
              <span>NET INCOME</span>
              <span style={{ display: 'flex', gap: 32 }}>
                <span>{fmt(is.totals.netIncome)}</span>
                <span style={{ color: '#cbd5e1', fontSize: 13 }}>{fmt(is.prior.netIncome)}</span>
              </span>
            </div>
          </>
        )}

        {activeReport === 't12' && t12 && (
          <>
            <h3 style={{ marginTop: 0, color: COLORS.dark, fontSize: 18 }}>Trailing 12 Months — Income Statement</h3>
            <div style={{ overflowX: 'auto' }}>
              <table data-testid="consolidated-t12-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${COLORS.dark}` }}>
                    <th style={{ textAlign: 'left', padding: '6px', minWidth: 180 }}>Account</th>
                    {t12.months.map((m) => (
                      <th key={m} style={{ textAlign: 'right', padding: '6px', minWidth: 70, color: COLORS.muted }}>{m}</th>
                    ))}
                    <th style={{ textAlign: 'right', padding: '6px', minWidth: 80, fontWeight: 700, color: COLORS.dark }}>T12 Total</th>
                  </tr>
                </thead>
                <tbody>
                  {t12.sections.map((sec, si) => {
                    if (sec.isMargin) {
                      return (
                        <tr key={`m-${si}`} style={{ borderTop: `1px solid ${COLORS.border}`, fontWeight: 600, color: COLORS.muted, fontStyle: 'italic' }}>
                          <td style={{ padding: '6px' }}>{sec.label}</td>
                          {sec.monthsTotal.map((v, i) => (
                            <td key={i} style={{ textAlign: 'right', padding: '6px' }}>
                              {v === null || v === undefined ? '—' : `${v.toFixed(1)}%`}
                            </td>
                          ))}
                          <td style={{ textAlign: 'right', padding: '6px', fontWeight: 700 }}>
                            {sec.total === null || sec.total === undefined ? '—' : `${sec.total.toFixed(1)}%`}
                          </td>
                        </tr>
                      );
                    }
                    const hasRows = sec.rows && sec.rows.length > 0;
                    return (
                      <React.Fragment key={`s-${si}`}>
                        <tr style={{ borderTop: `1px solid ${COLORS.dark}`, fontWeight: 700, background: COLORS.subtle }}>
                          <td colSpan={t12.months.length + 2} style={{ padding: '6px', color: COLORS.dark }}>{sec.label}</td>
                        </tr>
                        {hasRows && sec.rows.map((row, ri) => {
                          if (row.kind === 'group_header') {
                            return (
                              <tr key={`r-${si}-${ri}`} style={{ fontWeight: 600 }}>
                                <td style={{ padding: '4px 6px', paddingLeft: 16 }}>{row.label}</td>
                                {t12.months.map((_, i) => <td key={i}></td>)}
                                <td></td>
                              </tr>
                            );
                          }
                          return (
                            <tr key={`r-${si}-${ri}`} style={{ borderBottom: `1px solid ${COLORS.subtle}`,
                                                                fontWeight: row.kind === 'group_subtotal' ? 600 : 400 }}>
                              <td style={{ padding: '4px 6px', paddingLeft: row.kind === 'group_subtotal' ? 24 : (row.kind === 'leaf' ? 24 : 16) }}>{row.label}</td>
                              {(row.months || []).map((v, i) => (
                                <td key={i} style={{ textAlign: 'right', padding: '4px 6px' }}>{fmt(v)}</td>
                              ))}
                              <td style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>{fmt(row.total)}</td>
                            </tr>
                          );
                        })}
                        {(sec.isSubtotal || !hasRows) && (
                          <tr style={{ fontWeight: 700, borderTop: `1px solid ${COLORS.dark}` }}>
                            <td style={{ padding: '6px', paddingLeft: 16 }}>{sec.isSubtotal ? sec.label : `Total ${sec.label}`}</td>
                            {(sec.monthsTotal || []).map((v, i) => (
                              <td key={i} style={{ textAlign: 'right', padding: '6px' }}>{fmt(v)}</td>
                            ))}
                            <td style={{ textAlign: 'right', padding: '6px' }}>{fmt(sec.total)}</td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showPrintModal && (
        <PrintModal
          isOpen
          onClose={() => setShowPrintModal(false)}
          accounts={[]}
          client={printClient}
          mapping={memberPayloads[0]?.mapping || {}}
          ttmData={null}
          qboMeta={{ period_end: periodEnd }}
          userRole="staff"
          consolidatedReports={consolidated}
          consolidatedMembers={memberPayloads.map((m) => m.client)}
        />
      )}
    </div>
  );
}
